import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Trophy, Users, Calendar, DollarSign, Award, Lock, Loader2 } from "lucide-react";
import apiClient from "@/lib/apiClient";
import { PreviewLayout } from "@/components/PreviewLayout";
import { SignInGateModal } from "@/components/SignInGateModal";
import { EmptyState, FullPageLoader } from "@/components/common";

export default function PreviewTournaments() {
  const [flagsChecked, setFlagsChecked] = useState(false);
  const [allowed, setAllowed] = useState(false);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gateOpen, setGateOpen] = useState(false);

  useEffect(() => {
    apiClient.get("/public/guest-flags")
      .then(({ data }) => setAllowed(!!data.browse_tournaments))
      .catch(() => setAllowed(false))
      .finally(() => setFlagsChecked(true));
  }, []);

  useEffect(() => {
    if (!allowed) return;
    apiClient.get("/tournaments").then(({ data }) => setTournaments(data)).catch(() => {}).finally(() => setLoading(false));
  }, [allowed]);

  if (!flagsChecked) return <FullPageLoader label="Loading" />;
  if (!allowed) return <Navigate to="/login" replace />;

  return (
    <PreviewLayout tab="tournaments">
      <div className="mb-6">
        <h1 className="font-display text-3xl sm:text-4xl font-black text-white uppercase tracking-wide">Tournaments</h1>
        <p className="text-slate-400 mt-1.5">Browse upcoming events. Sign in to register and compete.</p>
      </div>

      {loading ? (
        <div className="text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading tournaments…</div>
      ) : tournaments.length === 0 ? (
        <EmptyState icon={Trophy} title="No tournaments scheduled" subtitle="Check back soon — new events drop regularly." />
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          {tournaments.map((t) => {
            const full = (t.participants?.length || 0) >= t.max_participants;
            const deadlinePassed = t.registration_deadline && new Date(t.registration_deadline) < new Date();
            const closed = t.status !== "upcoming" || deadlinePassed;
            return (
              <div key={t.id} className="glass glass-hover rounded-2xl p-6 flex flex-col" data-testid={`preview-tournament-${t.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: "rgba(255,176,32,0.15)", border: "1px solid rgba(255,176,32,0.4)" }}>
                      <Trophy className="w-6 h-6" style={{ color: "#ffb020" }} />
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold text-white">{t.name}</h3>
                      <p className="text-slate-400 text-sm">{t.game_type}</p>
                    </div>
                  </div>
                  <span className="chip capitalize" style={{ background: "rgba(0,229,255,0.12)", border: "1px solid rgba(0,229,255,0.4)", color: "#00e5ff" }}>{t.status}</span>
                </div>
                {t.description && <p className="text-slate-400 text-sm mb-4">{t.description}</p>}
                <div className="grid grid-cols-2 gap-3 text-sm mb-5">
                  <Info icon={Award} label="Prize Pool" value={`$${t.prize_pool}`} color="#00ff9d" />
                  <Info icon={DollarSign} label="Entry Fee" value={t.entry_fee > 0 ? `$${t.entry_fee}` : "Free"} color="#ffb020" />
                  <Info icon={Users} label="Players" value={`${t.participants?.length || 0}/${t.max_participants}`} color="#00e5ff" />
                  <Info icon={Calendar} label="Starts" value={t.start_datetime ? new Date(t.start_datetime).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "TBD"} color="#ff2e97" />
                </div>
                <div className="mt-auto">
                  <button onClick={() => setGateOpen(true)}
                    disabled={closed || full}
                    className="neon-outline-btn w-full py-3"
                    data-testid={`preview-tournament-register-${t.id}`}>
                    <Lock className="w-4 h-4" /> {full ? "Tournament Full" : closed ? "Registration Closed" : "Sign in to Register"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <SignInGateModal
        open={gateOpen}
        onClose={() => setGateOpen(false)}
        feature="register for tournaments"
        message="Create a free account (or sign in) to register for tournaments, track your standings and earn rewards."
      />
    </PreviewLayout>
  );
}

const Info = ({ icon: Icon, label, value, color }) => (
  <div className="flex items-center gap-2">
    <Icon className="w-4 h-4" style={{ color }} />
    <div>
      <p className="text-slate-500 text-xs">{label}</p>
      <p className="text-white font-semibold">{value}</p>
    </div>
  </div>
);
